import { bdLocationService, type BdDivision, type BdDistrict, type BdUpazila, type BdUnion } from '../services/httpServices/bdLocationService';

/**
 * Address fields shared by the permanent and present addresses. The BD
 * dropdowns used to save the upstream dataset ids (`3`, `23`, ...) instead of
 * the readable names; documents must never print those codes, so every helper
 * below ignores numeric division/district/upazila/union values. Any value that
 * is not a bare number is treated as an already-resolved name (including free
 * text for non-Bangladesh addresses).
 */
export interface PatientAddressParts {
  fullAddress?: string | null;
  country?: string | null;
  division?: string | null;
  district?: string | null;
  upazila?: string | null;
  unionCity?: string | null;
  area?: string | null;
  postalCode?: string | null;
}

export interface PatientAddressSource {
  permFullAddress?: string | null;
  permCountry?: string | null;
  permDivision?: string | null;
  permDistrict?: string | null;
  permUpazila?: string | null;
  permUnionCity?: string | null;
  permArea?: string | null;
  permPostalCode?: string | null;

  sameAsPermanent?: boolean;

  presFullAddress?: string | null;
  presCountry?: string | null;
  presDivision?: string | null;
  presDistrict?: string | null;
  presUpazila?: string | null;
  presUnionCity?: string | null;
  presArea?: string | null;
  presPostalCode?: string | null;
}

const NUMERIC_LOCATION_CODE = /^\d+$/;

export function isLegacyLocationCode(value?: string | null): boolean {
  return value != null && NUMERIC_LOCATION_CODE.test(String(value).trim());
}

const text = (value?: string | null): string => String(value ?? '').trim();

/** Trims a location name and drops legacy numeric dataset ids. */
const name = (value?: string | null): string => {
  const value_ = text(value);
  return NUMERIC_LOCATION_CODE.test(value_) ? '' : value_;
};

export function permanentAddressParts(patient: PatientAddressSource): PatientAddressParts {
  return {
    fullAddress: patient.permFullAddress,
    country: patient.permCountry,
    division: patient.permDivision,
    district: patient.permDistrict,
    upazila: patient.permUpazila,
    unionCity: patient.permUnionCity,
    area: patient.permArea,
    postalCode: patient.permPostalCode,
  };
}

export function presentAddressParts(patient: PatientAddressSource): PatientAddressParts {
  return {
    fullAddress: patient.presFullAddress,
    country: patient.presCountry,
    division: patient.presDivision,
    district: patient.presDistrict,
    upazila: patient.presUpazila,
    unionCity: patient.presUnionCity,
    area: patient.presArea,
    postalCode: patient.presPostalCode,
  };
}

/**
 * One-line address. The typed "full address" wins when present; otherwise the
 * structured parts are joined from the smallest unit outwards, matching the
 * order used across the dashboard.
 */
export function composePatientAddress(parts: PatientAddressParts): string {
  const fullAddress = text(parts.fullAddress);
  if (fullAddress) return fullAddress;

  return [
    text(parts.area),
    name(parts.unionCity),
    name(parts.upazila),
    name(parts.district),
    name(parts.division),
    text(parts.postalCode),
    text(parts.country),
  ]
    .filter(Boolean)
    .join(', ');
}

/**
 * The structured parts as labelled rows, e.g. `Permanent Division: Dhaka`.
 * Returns only fields that hold printable data.
 */
export function patientAddressDetailRows(
  parts: PatientAddressParts,
  prefix?: string,
): Array<{ label: string; value: string }> {
  const fields: Array<[string, string]> = [
    ['Country', text(parts.country)],
    ['Division', name(parts.division)],
    ['District', name(parts.district)],
    ['Upazila', name(parts.upazila)],
    ['Union / City', name(parts.unionCity)],
    ['Area', text(parts.area)],
    ['Postal Code', text(parts.postalCode)],
  ];

  return fields
    .filter(([, value]) => value.length > 0)
    .map(([label, value]) => ({
      label: prefix ? `${prefix} ${label}` : label,
      value,
    }));
}

// ─── Legacy id resolution ────────────────────────────────────────────────────
// Records created before the form stored names keep BD-API ids. Resolve them
// for one-off consumers such as the browser print window, which builds its HTML
// synchronously. The API results are memoised for the session.

let divisionsPromise: Promise<BdDivision[]> | null = null;
const districtsByDivision = new Map<string, Promise<BdDistrict[]>>();
const upazilasByDistrict = new Map<string, Promise<BdUpazila[]>>();
const unionsByUpazila = new Map<string, Promise<BdUnion[]>>();

const loadDivisions = () => {
  divisionsPromise ??= bdLocationService.getDivisions().catch(() => []);
  return divisionsPromise;
};

const loadDistricts = (divisionId: string) => {
  let promise = districtsByDivision.get(divisionId);
  if (!promise) {
    promise = bdLocationService.getDistrictsByDivision(divisionId).catch(() => []);
    districtsByDivision.set(divisionId, promise);
  }
  return promise;
};

const loadUpazilas = (districtId: string) => {
  let promise = upazilasByDistrict.get(districtId);
  if (!promise) {
    promise = bdLocationService.getUpazilasByDistrict(districtId).catch(() => []);
    upazilasByDistrict.set(districtId, promise);
  }
  return promise;
};

const loadUnions = (upazilaId: string) => {
  let promise = unionsByUpazila.get(upazilaId);
  if (!promise) {
    promise = bdLocationService.getUnionsByUpazila(upazilaId).catch(() => []);
    unionsByUpazila.set(upazilaId, promise);
  }
  return promise;
};

/** Finds the dataset id for a stored value that may be an id or a name. */
async function resolveId(
  value: string | null | undefined,
  loadRows: () => Promise<Array<{ id: string; name: string }>>,
): Promise<{ id: string; name: string } | null> {
  const raw = text(value);
  if (!raw) return null;
  const rows = await loadRows();
  const match = rows.find((row) => row.name === raw) ?? rows.find((row) => row.id === raw);
  return match ? { id: match.id, name: match.name } : null;
}

async function resolveAddressNames(parts: PatientAddressParts): Promise<PatientAddressParts> {
  // Nothing to do when every part is already a name (the normal case).
  if (![parts.division, parts.district, parts.upazila, parts.unionCity].some(isLegacyLocationCode)) {
    return parts;
  }

  const division = await resolveId(parts.division, loadDivisions);
  const district = division
    ? await resolveId(parts.district, () => loadDistricts(division.id))
    : null;
  const upazila = district
    ? await resolveId(parts.upazila, () => loadUpazilas(district.id))
    : null;
  const union = upazila ? await resolveId(parts.unionCity, () => loadUnions(upazila.id)) : null;

  return {
    ...parts,
    division: division?.name ?? parts.division,
    district: district?.name ?? parts.district,
    upazila: upazila?.name ?? parts.upazila,
    unionCity: union?.name ?? parts.unionCity,
  };
}

const LOOKUP_FIELDS: Array<keyof PatientAddressParts> = [
  'division',
  'district',
  'upazila',
  'unionCity',
];

/**
 * Returns a copy of the patient whose legacy numeric location ids have been
 * translated to names. Used before printing/downloading a record so documents
 * never fall back to raw dataset codes.
 */
export async function resolvePatientAddressNames<T extends PatientAddressSource>(patient: T): Promise<T> {
  const permanent = await resolveAddressNames(permanentAddressParts(patient));
  const present = patient.sameAsPermanent
    ? permanent
    : await resolveAddressNames(presentAddressParts(patient));

  const resolved = { ...patient } as unknown as Record<string, unknown>;
  for (const field of LOOKUP_FIELDS) {
    resolved[`perm${field[0].toUpperCase()}${field.slice(1)}`] = permanent[field] ?? undefined;
    resolved[`pres${field[0].toUpperCase()}${field.slice(1)}`] = present[field] ?? undefined;
  }
  return resolved as T;
}
